/**
 * Every user-facing string of the assisted list creation (`/admin/nova-lista`), in one place so
 * the copy review reads it side by side with the requirements.
 *
 * Quoted texts are literal from the `interno` branch of the approved prototype
 * (`onboarding-auth-flow.jsx`); the ones the prototype never drew, or that were adapted to what
 * the backend delivers, come from the "Copy nova" table of the spec's requirements.
 *
 * Titles carry the prototype's `<em>` accent as an {@link AccentTitle}. Texts with placeholders
 * are small `format*` functions below the table; they take values already formatted (masked CPF,
 * date in full) so this module stays plain data with no runtime imports.
 *
 * The event block's field words are the exception, and deliberately: they come from
 * `lib/event-form`, the single source the three screens that ask for the same seven things now
 * share. That module was seeded from here, so no string changed — see `EVENT_FORM_COPY`.
 */

import { EVENT_FORM_COPY } from "@/lib/event-form/event-form";
/**
 * A heading with one accented phrase, rendered as `{before} <Emphasis>{accent}</Emphasis> {after}`
 * with single spaces between the non-empty parts.
 */
export type AccentTitle = {
  readonly before: string;
  readonly accent: string;
  readonly after: string;
};
/** Who the ledes name when the Customer's first name is empty. */
const CUSTOMER_FALLBACK_NAME = "o cliente";
export const NOVA_LISTA_COPY = {
  /** The CpfStepForm voice and its messages (Req. 3.2 to 3.5) and the resume notice (Req. 4.1). */
  cpf: {
    backLink: "voltar ao painel",
    eyebrow: "Nova lista · atendimento em loja",
    title: { before: "Informe o", accent: "CPF", after: "do cliente" },
    lede: "Ele identifica o cliente de forma única. Se já existe cadastro, os dados vêm prontos; se não, o cadastro do cliente nasce aqui.",
    fieldLabel: "CPF do cliente",
    help: "A lista nasce vinculada a este CPF, e é nele que os créditos se acumulam.",
    submit: "Consultar CPF",
    cnpjMessage: "Isso parece um CNPJ: a lista é criada com o CPF do cliente.",
    incompleteMessage: "Informe os onze dígitos do CPF do cliente.",
    invalidMessage: "Esse CPF não confere. Confira os números e tente de novo.",
    resumeNotice: "Você já começou uma lista em rascunho para este cliente.",
    resumeAction: "Retomar a criação",
  },
  /** Step names of the Staff_Wizard, keyed by the reducer's `step`; "Endereço" only without address. */
  steps: {
    cpf: "CPF",
    dados: "Dados",
    endereco: "Endereço",
    evento: "Evento",
  },
  /** The data step over SignupStepForm `variant="staff"` (Req. 5.2 to 5.5). */
  data: {
    backLink: "corrigir o CPF",
    existingCustomer: {
      eyebrow: "Cliente encontrado na base",
      title: { before: "Confira os dados do", accent: "cliente", after: "" },
      lede: "O cadastro já existe e os dados vieram preenchidos. Ajuste só o que estiver desatualizado: é pelo e-mail que o cliente recebe o termo.",
      submit: "Confirmar dados e continuar",
    },
    newCustomer: {
      eyebrow: "Cadastro do cliente · dados",
      title: { before: "Cadastrar novo", accent: "cliente", after: "" },
      lede: "Preencha com o cliente ao lado. Ao salvar, ele recebe o e-mail de boas-vindas com o primeiro acesso à conta.",
      submit: "Salvar cadastro e continuar",
    },
    placeholders: {
      firstName: "Primeiro nome do cliente",
      lastName: "O restante do nome",
      email: "E-mail do cliente",
    },
    errors: {
      firstName: "Informe o nome do cliente.",
      lastName: "Informe o sobrenome do cliente.",
      phone: "Informe um celular com DDD.",
      email: "Informe um e-mail válido.",
    },
    /** `emailHelp` of the read-only e-mail of an existing Customer (Req. 5.5). */
    emailReadOnlyHelp: "O e-mail do cadastro não muda por aqui: é por ele que o cliente recebe o termo.",
    /** The submit while an existing Customer's PATCH is in flight; the host's pending word. */
    pending: "Salvando...",
  },
  /** The address step over AddressStepForm `showUsage={false}` (Req. 6.3). */
  address: {
    backLink: "voltar",
    eyebrow: "Cadastro do cliente · endereço",
    title: { before: "Onde a lista do cliente", accent: "mora", after: "" },
    lede: "É para cá que enviaremos as peças escolhidas pelos convidados do cliente.",
    /** The prototype's address submit (`ScreenAddress`), for a new Customer and an existing one alike. */
    submit: "Salvar endereço e continuar",
    /** The submit while the Customer or the address is being created; the host's pending word. */
    pending: "Salvando...",
  },
  /** The InternalEventStep (Req. 8.1 to 8.5); no "Número de convidados" (Req. 8.7). */
  event: {
    backLink: "voltar",
    eyebrow: "Nova lista · evento",
    title: { before: "Informações do", accent: "evento", after: "" },
    lede: "O que o convidado lê ao abrir a lista do cliente. O nome da lista nasce dos nomes dos participantes do evento.",
    submit: "Salvar e escolher as peças",
    /** The submit while `POST /gift-lists` is in flight; the host's pending word. */
    pending: "Salvando...",
    /*
     * The field words come from the shared event form (`lib/event-form`), which was seeded from
     * these very objects. Referenced rather than repeated so the three screens that ask for the
     * same seven things cannot drift apart again — which is how the host's wizard ended up
     * asking for name and surname in a single field.
     *
     * What stays above — eyebrow, title, lede, submit — is what the screens legitimately say
     * differently: this one speaks of "o cliente".
     */
    labels: {
      recipientFirstName: EVENT_FORM_COPY.labels.recipientFirstName,
      recipientLastName: EVENT_FORM_COPY.labels.recipientLastName,
      otherPartyFirstName: EVENT_FORM_COPY.labels.otherPartyFirstName,
      otherPartyLastName: EVENT_FORM_COPY.labels.otherPartyLastName,
      listName: EVENT_FORM_COPY.labels.listName,
      welcomeMessage: EVENT_FORM_COPY.labels.welcomeMessage,
      eventDate: EVENT_FORM_COPY.labels.eventDate,
      eventLocation: EVENT_FORM_COPY.labels.eventLocation,
    },
    placeholders: {
      firstName: EVENT_FORM_COPY.placeholders.firstName,
      lastName: EVENT_FORM_COPY.placeholders.lastName,
      /** Shown in the read-only "Nome da lista" while both parties are empty. */
      listName: EVENT_FORM_COPY.placeholders.listName,
      eventLocation: EVENT_FORM_COPY.placeholders.eventLocation,
    },
    help: {
      listName: EVENT_FORM_COPY.help.listName,
      welcomeMessage: EVENT_FORM_COPY.help.welcomeMessage,
      eventLocation: EVENT_FORM_COPY.help.eventLocation,
    },
    /**
     * The past-date message is not here: it comes from the date schema of `lib/create-list`
     * (Req. 8.6).
     *
     * Keys spelled out one by one, and not the shared object handed over whole: a test walks
     * `Object.keys(EVENT_ERRORS)` and asserts that **every** key is a field of
     * `internalEventSchema` with that message (`nova-lista-schemas.test.ts:235`). The shared
     * object carries words this flow does not ask for — the guest count, and the "too long"
     * pairs — so handing it over whole would break that invariant. Same strings, same keys,
     * one source.
     */
    errors: {
      recipientFirstName: EVENT_FORM_COPY.errors.recipientFirstName,
      recipientLastName: EVENT_FORM_COPY.errors.recipientLastName,
      otherPartyFirstName: EVENT_FORM_COPY.errors.otherPartyFirstName,
      otherPartyLastName: EVENT_FORM_COPY.errors.otherPartyLastName,
      welcomeMessage: EVENT_FORM_COPY.errors.welcomeMessage,
      eventDate: EVENT_FORM_COPY.errors.eventDate,
      eventLocation: EVENT_FORM_COPY.errors.eventLocation,
    },
  },
  /** The StoreAdministratorPicker (Req. 7.1, 7.5). */
  picker: {
    storeLabel: "Loja da lista",
    administratorLabel: "Administrador da lista",
    /** The empty option of both selects, as the host's own selects read. */
    selectPlaceholder: "Selecione",
    /** The selects' only option while their options load. */
    loading: "Carregando...",
    /** "Administrador da lista" before a store is chosen: its options are that store's users. */
    chooseStoreFirst: "Escolha a loja primeiro",
    /** The chosen store has no active user who can administer the list. */
    noAdministrators: "Nenhum usuário ativo nesta loja.",
    /** An administrator option whose user has no name in the backend. */
    unnamedAdministrator: "Usuário sem nome",
    /** Reloads the stores or the users after a failed read, the app's retry word. */
    retry: "Tentar novamente",
    errors: {
      store: "Escolha a loja da lista.",
      administrator: "Escolha quem administra a lista.",
    },
  },
  /** The StartingPointStep (Req. 10.1); its title is {@link formatStartingPointTitle}. */
  startingPoint: {
    eyebrow: "Nova lista · ponto de partida",
    titleAccent: "começa",
    lede: "Monte peça por peça no catálogo completo, ou parta de uma lista de sugestão da Tania Bulhões para acelerar a curadoria. Nos dois caminhos, tudo se ajusta depois.",
    fromScratch: "Criar lista do zero",
    fromSuggestion: "Utilizar uma lista de sugestão",
  },
  /** The CurationStep over the staff token (Req. 10.6, 10.7). */
  curation: {
    /**
     * The catalog search, said as §6.2 says it for the internal area: SKU where the host reads
     * "coleção". The staff look a piece up by its code, and the served catalogue does match it
     * (the API searches `name`, `sku`, `slug` and the descriptions).
     */
    searchPlaceholder: "Busque por nome da peça ou SKU",
  },
  /** The header slot of SuggestionChoiceStep and the CTA of SuggestionPreviewStep (Req. 10.3, 10.4). */
  suggestions: {
    choice: {
      backLink: "voltar ao ponto de partida",
      eyebrow: "Listas de Sugestão",
      title: { before: "Vista sua casa com escolhas", accent: "essenciais", after: "de Tania." },
      lede: "Uma curadoria assinada por Tania Bulhões. Abra uma lista para ver as peças e utilizá-la como ponto de partida do cliente, ou monte do zero no catálogo.",
      fromScratch: "Montar do zero no catálogo",
    },
    preview: {
      adoptLabel: "Utilizar esta lista",
    },
  },
  /** The HandoffStep (Req. 11.1 to 11.8); its lede is {@link formatHandoffLede}. */
  handoff: {
    backLink: "voltar à curadoria",
    eyebrow: "Nova lista · passagem de bastão",
    title: { before: "Revise e envie para o", accent: "cliente", after: "" },
    sections: {
      summary: "Resumo da curadoria",
      event: "Evento",
      delivery: "Envio para o cliente",
    },
    labels: {
      items: "Itens adicionados",
      recipient: "Quem recebe",
      otherParty: "Outra parte",
      date: "Data",
      location: "Local",
      email: "E-mail",
    },
    /** Value of an empty "Quem recebe", "Outra parte", "Data" or "Local". */
    emptyValue: "a definir",
    noEmail: "sem e-mail",
    submit: "Concluir e enviar comunicação para o cliente",
    submitHelp: "Nada é publicado agora: a publicação é do cliente, depois do aceite.",
    emptyListError: "A lista não pode seguir vazia: volte à curadoria e escolha as peças.",
    /** The Handoff answered 409: the communication already went out (Req. 11.7). */
    alreadySent: {
      notice: "A comunicação já foi enviada para este cliente.",
      action: "Reenviar a comunicação",
      confirmDialog: {
        title: "Reenviar a comunicação?",
        description: "O cliente recebe um novo e-mail com o link de aprovação.",
        confirmLabel: "Reenviar",
        cancelLabel: "Cancelar",
      },
    },
    emailMissing: "Este cliente não tem e-mail no cadastro, e o link de aprovação segue por e-mail.",
  },
  /** The ActiveListModal (Req. 4.3 to 4.5); its lede is {@link formatActiveListLede}. */
  activeListModal: {
    closeLabel: "Fechar",
    eyebrow: "Criação bloqueada",
    title: { before: "Este cliente já tem uma lista", accent: "em andamento", after: "" },
    /** The lede without list data, also used after a 409 on `POST /gift-lists` (Req. 4.6). */
    shortLede: "Um cliente tem uma lista ativa por vez.",
    fixCpf: "Corrigir o CPF",
  },
  /** The SentModal (Req. 11.9, 11.10); its lede is {@link formatSentLede}. */
  sentModal: {
    eyebrow: "Lista em rascunho",
    title: { before: "Comunicação", accent: "enviada", after: "" },
    backToPanel: "Voltar ao painel",
  },
  /** The Assisted_Creation_Bar (Req. 2.1, 2.2); its sentence is {@link formatAssistedBarText}. */
  bar: {
    regionLabel: "Criação assistida",
    eyebrow: "Criação assistida",
    lead: "Você está criando uma lista para:",
    unidentifiedCustomer: "cliente a identificar",
    cpfLabel: "CPF",
    separator: "·",
    cancel: "Cancelar e voltar ao painel",
  },
  errors: {
    /** When the backend gives no message of its own (Req. 6.6). */
    generic: "Não foi possível concluir agora. Tente de novo em instantes.",
    /** `POST customers` answered 409 `EMAIL_ALREADY_EXISTS`: the address step keeps its values (Req. 6.6). */
    emailAlreadyExists: "Este e-mail já está em uso por outro cliente. Volte aos dados e informe outro e-mail.",
    /** `POST customers` answered 409 `CPF_ALREADY_REGISTERED`: the CPF got a Customer since the lookup (Req. 6.6). */
    cpfAlreadyRegistered: "Este CPF já tem cadastro. Volte ao CPF e consulte de novo.",
    /** On `/admin/nova-lista?indisponivel=1`, after the `[listId]` guard refuses a list (Req. 9.2). */
    listUnavailable: "Esta lista não está disponível para a criação assistida.",
  },
} as const;
/** Trims a name and falls back to "o cliente" when nothing is left. */
function resolveCustomerName(firstName: string | null | undefined): string {
  const trimmed = (firstName ?? "").trim();
  return trimmed === "" ? CUSTOMER_FALLBACK_NAME : trimmed;
}
/** Joins the non-empty parts of an {@link AccentTitle} with single spaces, for accessible names and tests. */
export function toPlainTitle(title: AccentTitle): string {
  return [title.before, title.accent, title.after].filter((part) => part !== "").join(" ");
}
/**
 * "Como a lista de {nome} *começa*" (Req. 10.1). Without a first name the prototype drops the
 * "de {nome}" part: "Como a lista *começa*".
 */
export function formatStartingPointTitle(firstName: string | null | undefined): AccentTitle {
  const trimmed = (firstName ?? "").trim();
  const before = trimmed === "" ? "Como a lista" : `Como a lista de ${trimmed}`;
  return { before, accent: NOVA_LISTA_COPY.startingPoint.titleAccent, after: "" };
}
/** The HandoffStep lede; an empty first name reads "o cliente" (Copy nova). */
export function formatHandoffLede(firstName: string | null | undefined): string {
  return `A lista fica em rascunho até ${resolveCustomerName(firstName)} aceitar os termos de uso e publicar. O link de aprovação segue por e-mail.`;
}
/** The SentModal lede; an empty first name reads "o cliente" (Copy nova). */
export function formatSentLede(firstName: string | null | undefined): string {
  return `Lista salva em Rascunho. Um e-mail foi enviado para ${resolveCustomerName(firstName)} com o link de aprovação.`;
}
/** What the ActiveListModal lede needs to name the list; every value already formatted. */
export type ActiveListLedeDetails = {
  /** "{nome} {sobrenome}" of the list's owner. */
  readonly ownerName?: string | null;
  readonly listTitle?: string | null;
  /** The event date in full, e.g. `formatCivilDateInFull(eventDate)` from `lib/civil-date`. */
  readonly eventDateInFull?: string | null;
};
/**
 * The ActiveListModal lede (Copy nova): "{nome} {sobrenome} administra a lista {lista}, com
 * evento em {data por extenso}. Um cliente tem uma lista ativa por vez." Without the list data
 * (no details, or any value empty) it is only the short lede.
 */
export function formatActiveListLede(details: ActiveListLedeDetails | null): string {
  const ownerName = (details?.ownerName ?? "").trim();
  const listTitle = (details?.listTitle ?? "").trim();
  const eventDateInFull = (details?.eventDateInFull ?? "").trim();
  const { shortLede } = NOVA_LISTA_COPY.activeListModal;
  if (ownerName === "" || listTitle === "" || eventDateInFull === "") return shortLede;
  return `${ownerName} administra a lista ${listTitle}, com evento em ${eventDateInFull}. ${shortLede}`;
}
/** "Itens adicionados" value (Req. 11.2): "Nenhuma peça", "1 peça" or "{n} peças". */
export function formatItemsCount(count: number): string {
  if (count <= 0) return "Nenhuma peça";
  return count === 1 ? "1 peça" : `${count} peças`;
}
/** What the Assisted_Creation_Bar names. */
export type AssistedBarSubject = {
  /** The Customer's name; empty or absent reads "cliente a identificar" (Req. 2.2). */
  readonly customerName?: string | null;
  /** The CPF already masked as `000.000.000-00`; absent until a CPF was looked up (Req. 2.2). */
  readonly maskedCpf?: string | null;
};
/**
 * The bar's sentence as plain text (Req. 2.1, 2.2): "Você está criando uma lista para: {cliente}
 * · CPF {cpf}", without the CPF part until there is one. The component styles the parts from
 * `NOVA_LISTA_COPY.bar`; this is the same sentence for accessible names and tests.
 */
export function formatAssistedBarText({ customerName, maskedCpf }: AssistedBarSubject): string {
  const { lead, unidentifiedCustomer, cpfLabel, separator } = NOVA_LISTA_COPY.bar;
  const name = (customerName ?? "").trim();
  const cpf = (maskedCpf ?? "").trim();
  const subject = `${lead} ${name === "" ? unidentifiedCustomer : name}`;
  return cpf === "" ? subject : `${subject} ${separator} ${cpfLabel} ${cpf}`;
}
